import React, { useEffect, useState } from "react";
import "../assets/properties.css";
import styles from "../assets/properties.module.css";

import { toast } from "react-toastify";
import { useAuth } from "../contexts/AuthContext";
import Loader from "../components/Loader";

// Data-protection restriction: only these users may export properties.
const PROPERTIES_EXPORT_ALLOWED_EMAILS = [
  "stuart.woolgar+sm@global-guardians.co.uk",
  "lauren.boswell@global-guardians.co.uk",
  "vikaila.vekaria@global-guardians.co.uk",
];

// Property restore is temporarily restricted to this account only.
const PROPERTY_RESTORE_ALLOWED_EMAILS = ["global.guardians@theaimegroup.com"];

const SortIcon = ({ direction, isActive }) => (
  <svg
    width="10"
    height="16"
    viewBox="0 0 10 16"
    style={{ marginLeft: 4, verticalAlign: "middle" }}
  >
    <g fill={isActive ? "#333" : "#bbb"}>
      {direction === "asc" ? (
        <polygon points="5,3 8,7 2,7" />
      ) : direction === "desc" ? (
        <polygon points="5,13 2,9 8,9" />
      ) : (
        <>
          <polygon points="5,3 8,7 2,7" />
          <polygon points="5,13 2,9 8,9" />
        </>
      )}
    </g>
  </svg>
);

// Status mapping
const STATUS_CARDS = [
  { key: "all", label: "All", color: "#d9534f" },
  { key: "pre_offer", label: "Pre-Offer", color: "#f5e6c8" },
  { key: "under_offer", label: "Under Offer", color: "#f7b7a3" },
  { key: "signed_proposal", label: "Signed Proposal", color: "#2e3cff" },
  { key: "maintenance", label: "Maintenance", color: "#f7c873" },
  { key: "empty", label: "Empty", color: "#b2e6b2" },
  { key: "not_full", label: "Not Full", color: "#b2ffb2" },
  { key: "full", label: "Full", color: "#43b143" },
  { key: "rejected_offer", label: "Rejected Offer", color: "#ff4d4d" },
  { key: "handing_back", label: "Handing Back", color: "#a94442" },
  { key: "ended", label: "Ended", color: "#222" },
  { key: "key_holding", label: "Key Holding", color: "#a259c6" },
  { key: "caretakers", label: "Caretakers", color: "#17a2b8" },
];

// All columns matching Laravel DataTable (35 total columns)
// Visible columns (6) + Hidden columns (29) + Actions (1) = 36 columns total
const allColumns = [
  // Visible columns (default) - 6 columns matching Laravel exactly
  { key: "property_name", label: "Property Name", visible: true },
  { key: "property_id", label: "Property ID", visible: true },
  { key: "company_name", label: "Company Name", visible: true },
  { key: "postcode", label: "Postcode", visible: true },
  { key: "website_display_name", label: "Website Name", visible: true },
  { key: "status", label: "Status", visible: true },
  { key: "notice_date", label: "Notice date", visible: true },
  
  // Hidden columns (for export and advanced filtering) - 27 columns matching Laravel
  { key: "company_contact", label: "Company contact", visible: false },
  { key: "price_from", label: "Price from", visible: false },
  { key: "price_to", label: "Price to", visible: false },
  { key: "actual_market_rent", label: "Actual market rent", visible: false },
  { key: "spaces", label: "Spaces", visible: false },
  { key: "bathrooms", label: "Bathrooms", visible: false },
  { key: "kitchens", label: "Kitchens", visible: false },
  { key: "garden", label: "Garden", visible: false },
  { key: "parking", label: "Parking", visible: false },
  { key: "vip_amount", label: "VIP amount", visible: false },
  { key: "vip_provided", label: "VIP provided", visible: false },
  { key: "savings_made", label: "Savings made", visible: false },
  { key: "date_taken_on", label: "Date take on", visible: false },
  { key: "date_live", label: "Date live", visible: false },
  { key: "date_taken_back", label: "Date taken back", visible: false },
  { key: "date_feature_from", label: "Date future from", visible: false },
  { key: "date_feature_to", label: "Date future to", visible: false },
  { key: "short_description", label: "Short description", visible: false },
  { key: "public_short_description", label: "Public short dsc", visible: false },
  { key: "address", label: "Address", visible: false },
  { key: "town", label: "Town", visible: false },
  { key: "geo_latitude", label: "Latitude", visible: false },
  { key: "geo_longitude", label: "Longitude", visible: false },
  { key: "video", label: "Video", visible: false },
  { key: "3d_mapping", label: "3D Mapping", visible: false },
  { key: "client_type", label: "Client type", visible: false },
  { key: "days_in_status", label: "Days in status", visible: false },
  { key: "account_manager", label: "Account Manager", visible: false },
];

const PropertyList = () => {
  const [properties, setProperties] = useState([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sortField, setSortField] = useState("property_name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [showColDropdown, setShowColDropdown] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(
    allColumns.filter((col) => col.visible !== false).map((col) => col.key)
  );
  const [initialLoading, setInitialLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [draw, setDraw] = useState(1);
  const [statusCounts, setStatusCounts] = useState({ all: 0 });
  const [blocks, setBlocks] = useState({}); // Blocks for dropdown: {id: name}
  const [selectedBlock, setSelectedBlock] = useState("");
  const [showDeleted, setShowDeleted] = useState(false);

  const { hasPermission, isGuardianManager, user } = useAuth();
  const canExportProperties =
    !!user?.email &&
    (PROPERTIES_EXPORT_ALLOWED_EMAILS.includes(user.email.trim().toLowerCase()) || user.email.trim().toLowerCase().endsWith("@kimonservices.com"));
  const canRestoreProperties =
    !!user?.email &&
    PROPERTY_RESTORE_ALLOWED_EMAILS.includes(user.email.trim().toLowerCase());

  // Server-side pagination calculations
  const totalPages = Math.ceil(totalRecords / pageSize);
  const startIndex = (currentPage - 1) * pageSize;

  // Map frontend sort field to backend column index
  const getSortColumnIndex = (field) => {
    const mapping = {
      "property_id": 0,
      "property_name": 1,
      "company_name": 2,
      "postcode": 3,
      "status": 4,
      "notice_date": 5,
    };
    return mapping[field] || 0;
  };

  // Fetch status overview for cards (using dedicated endpoint matching Laravel)
  const fetchStatusOverview = () => {
    fetch(`${process.env.REACT_APP_API_URL}/api/properties/status-overview`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    })
      .then((res) => res.json())
      .then((response) => {
        if (response.success && response.data) {
          // Use backend-calculated counts (matching Laravel PropertiesController@index)
          const counts = {
            all: response.data.all || 0,
            pre_offer: response.data.pre_offer || 0,
            under_offer: response.data.under_offer || 0,
            signed_proposal: response.data.signed_proposal || 0,
            maintenance: response.data.maintenance || 0,
            empty: response.data.empty || 0,
            not_full: response.data.not_full || 0,
            full: response.data.full || 0,
            rejected_offer: response.data.rejected_offer || 0,
            handing_back: response.data.handing_back || 0,
            ended: response.data.ended || 0,
            key_holding: response.data.key_holding || 0,
            caretakers: response.data.caretakers || 0,
          };
          setStatusCounts(counts);
          console.log("✅ [FRONTEND] Status overview loaded from backend:", counts);
        } else {
          console.warn("⚠️ [FRONTEND] Unexpected response format:", response);
          // Fallback to empty counts
          setStatusCounts({ all: 0 });
        }
      })
      .catch((error) => {
        console.error("❌ [FRONTEND] Error fetching status overview:", error);
        // Fallback to empty counts on error
        setStatusCounts({ all: 0 });
      });
  };

  // Helper function to map status to key
  function getStatusKey(status) {
    if (!status) return "all";
    const s = String(status).toLowerCase();
    if (s.includes("pre")) return "pre_offer";
    if (s.includes("under")) return "under_offer";
    if (s.includes("signed")) return "signed_proposal";
    if (s.includes("maint")) return "maintenance";
    if (s.includes("empty")) return "empty";
    if (s.includes("not full")) return "not_full";
    if (s.includes("full")) return "full";
    if (s.includes("rejected")) return "rejected_offer";
    if (s.includes("handing")) return "handing_back";
    if (s.includes("ended")) return "ended";
    if (s.includes("key")) return "key_holding";
    if (s.includes("caretaker")) return "caretakers";
    return s;
  }

  // Fetch properties with server-side DataTables pagination
  const fetchProperties = () => {
    const startTime = performance.now();
    console.log("🔍 [FRONTEND] Fetching properties with server-side pagination");
    setLoading(true);

    const datatableParams = {
      draw: draw,
      start: startIndex,
      length: pageSize,
      search: { value: debouncedSearch },
      order: [{ column: getSortColumnIndex(sortField), dir: sortDirection }],
      statusFilter: statusFilter === 'all' ? '' : statusFilter,
      visibleColumns: visibleColumns, // Send visible columns for dynamic search
      deletedOnly: showDeleted
    };

    console.log("📊 [FRONTEND] DataTables params:", datatableParams);

    fetch(`${process.env.REACT_APP_API_URL}/api/properties/datatable`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem("token")}`
      },
      body: JSON.stringify(datatableParams)
    })
      .then((res) => res.json())
      .then((data) => {
        const endTime = performance.now();
        const duration = (endTime - startTime).toFixed(0);
        console.log(`📊 [FRONTEND] DataTables response received in ${duration}ms:`, data);
        
        // Remove duplicates based on id (safety measure)
        const propertiesData = data.data || [];
        const uniqueProperties = propertiesData.filter((property, index, self) =>
          index === self.findIndex((p) => p.id === property.id)
        );

        if (uniqueProperties.length !== propertiesData.length) {
          console.log(
            `⚠️ [FRONTEND] Removed ${propertiesData.length - uniqueProperties.length} duplicate properties`
          );
        }

        setProperties(uniqueProperties);
        setTotalRecords(data.recordsFiltered || 0);
        setDraw(data.draw);
        setLoading(false);
        setInitialLoading(false);
      })
      .catch((error) => {
        console.error("❌ [FRONTEND] Error fetching properties:", error);
        setProperties([]);
        setTotalRecords(0);
        setLoading(false);
        setInitialLoading(false);
        toast.error("Failed to load properties. Please try again.");
      });
  };

  // Fetch blocks for dropdown (matching Laravel: Block::all()->sortBy('name')->pluck('name', 'id')->toArray())
  const fetchBlocks = () => {
    fetch(`${process.env.REACT_APP_API_URL}/api/properties/blocks`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    })
      .then((res) => res.json())
      .then((response) => {
        if (response.success && response.data) {
          setBlocks(response.data);
          console.log("✅ [FRONTEND] Blocks loaded:", Object.keys(response.data).length, "blocks");
        } else {
          console.warn("⚠️ [FRONTEND] Unexpected blocks response format:", response);
          setBlocks({});
        }
      })
      .catch((error) => {
        console.error("❌ [FRONTEND] Error fetching blocks:", error);
        setBlocks({});
      });
  };

  // Handle block selection for email guardians (matching Laravel: js-send-email-to-guardians-of-block)
  const handleBlockChange = (e) => {
    const blockId = e.target.value;
    setSelectedBlock(blockId);
    
    if (blockId && blockId !== "") {
      // Navigate to block email guardians page (Laravel: /blocks/{blockId}/emails/guardians/create)
      window.open(`/blocks/${blockId}/emails/guardians/create`, '_blank');
    }
  };

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setCurrentPage(1); // Reset to page 1 when search changes
    }, 400); // 400ms debounce delay - wait for user to stop typing

    return () => clearTimeout(timer);
  }, [search]);

  // Fetch status overview on mount
  useEffect(() => {
    fetchStatusOverview();
    // Fetch blocks if user has permission
    if (hasPermission && hasPermission("properties.emails.guardians")) {
      fetchBlocks();
    }
  }, [hasPermission]);

  // Fetch properties when dependencies change
  useEffect(() => {
    fetchProperties();
  }, [currentPage, pageSize, debouncedSearch, sortField, sortDirection, statusFilter, showDeleted]);

  // Handle pagination
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (e) => {
    setPageSize(parseInt(e.target.value));
    setCurrentPage(1);
  };

  // Handle search with pagination reset
  const handleSearchChange = (e) => {
    setSearch(e.target.value.trimStart());
    setCurrentPage(1);
  };

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Handle status filter
  const handleStatusFilter = (key) => {
    console.log('🔍 Status card clicked:', key);
    // Always apply the filter (no toggle - user expects consistent filter behavior)
    setStatusFilter(key);
    setCurrentPage(1);
  };

  // Toggle between active and removed properties (restore-account only)
  const handleToggleShowDeleted = () => {
    setShowDeleted((prev) => !prev);
    setCurrentPage(1);
    setStatusFilter("all");
  };

  // Restore a soft-deleted property and its related records
  const handleRestore = async (property) => {
    if (!canRestoreProperties) {
      toast.error("You are not authorised to restore properties.");
      return;
    }

    const confirmMessage = `Restore property "${property.name}" (ID ${property.property_id}) and all related records?`;
    if (!window.confirm(confirmMessage)) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/properties/${property.id}/restore`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      const result = await response.json();

      if (response.ok && result.success) {
        toast.success(result.message || "Property restored successfully.");
        fetchProperties();
      } else {
        toast.error(result.message || "Failed to restore property.");
      }
    } catch (error) {
      console.error("Error restoring property:", error);
      toast.error("Failed to restore property. Please try again.");
    }
  };

  // Render pagination controls
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxPagesToShow = 5;
    const halfRange = Math.floor(maxPagesToShow / 2);

    let startPage = Math.max(1, currentPage - halfRange);
    let endPage = Math.min(totalPages, currentPage + halfRange);

    if (currentPage <= halfRange) {
      endPage = Math.min(maxPagesToShow, totalPages);
    }
    if (currentPage + halfRange >= totalPages) {
      startPage = Math.max(1, totalPages - maxPagesToShow + 1);
    }

    if (startPage > 1) {
      pages.push(
        <button key={1} className={currentPage === 1 ? 'active' : ''} onClick={() => handlePageChange(1)}>
          1
        </button>
      );
      if (startPage > 2) {
        pages.push(<span key="ellipsis-start">...</span>);
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          className={currentPage === i ? 'active' : ''}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </button>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(<span key="ellipsis-end">...</span>);
      }
      pages.push(
        <button
          key={totalPages}
          className={currentPage === totalPages ? 'active' : ''}
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </button>
      );
    }

    return (
      <div style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 8 }}>
        <button disabled={currentPage === 1} onClick={() => handlePageChange(currentPage - 1)}>
          Prev
        </button>
        {pages}
        <button disabled={currentPage === totalPages} onClick={() => handlePageChange(currentPage + 1)}>
          Next
        </button>
      </div>
    );
  };

  // Helper function to get property value for any column (matching Laravel DataTable column mapping)
  const getPropertyValue = (property, columnKey) => {
    switch (columnKey) {
      case "property_name":
        return property.name || "";
      case "property_id":
        return property.property_id || "";
      case "company_name":
        return property.client?.name || "";
      case "postcode":
        return property.postcode || "";
      case "website_display_name":
        return property.website_display_name || "";
      case "status":
        return property.status_text || property.statusName || property.status || "";
      case "notice_date":
        return property.date_notice ? new Date(property.date_notice).toLocaleDateString('en-GB') : "";
      case "company_contact":
        return property.client_contact_name || property.clientContact?.user?.first_name + " " + property.clientContact?.user?.last_name || "No contact";
      case "price_from":
        return property.price_from ? `£${parseFloat(property.price_from).toFixed(2)}` : "";
      case "price_to":
        return property.price_to ? `£${parseFloat(property.price_to).toFixed(2)}` : "";
      case "actual_market_rent":
        return property.actual_market_rent ? `£${parseFloat(property.actual_market_rent).toFixed(2)}` : "";
      case "spaces":
        return property.spaces || "";
      case "bathrooms":
        return property.bathrooms || "";
      case "kitchens":
        return property.kitchens || "";
      case "garden":
        return property.garden ? (property.garden > 0 ? "Yes" : "No") : "No";
      case "parking":
        return property.parking ? (property.parking > 0 ? "Yes" : "No") : "No";
      case "vip_amount":
        return property.vip_amount ? `£${parseFloat(property.vip_amount).toFixed(2)}` : "";
      case "vip_provided":
        return property.vip_provided ? `£${parseFloat(property.vip_provided).toFixed(2)}` : "";
      case "savings_made":
        return property.savings_made ? `£${parseFloat(property.savings_made).toFixed(2)}` : "";
      case "date_taken_on":
        return property.date_taken_on ? new Date(property.date_taken_on).toLocaleDateString('en-GB') : "";
      case "date_live":
        return property.date_live ? new Date(property.date_live).toLocaleDateString('en-GB') : "";
      case "date_taken_back":
        return property.date_taken_back ? new Date(property.date_taken_back).toLocaleDateString('en-GB') : "";
      case "date_feature_from":
        return property.date_feature_from ? new Date(property.date_feature_from).toLocaleDateString('en-GB') : "";
      case "date_feature_to":
        return property.date_feature_to ? new Date(property.date_feature_to).toLocaleDateString('en-GB') : "";
      case "short_description":
        return property.short_description || "";
      case "public_short_description":
        return property.public_short_description || "";
      case "address":
        return property.address || property.address_1 || "";
      case "town":
        return property.town || "";
      case "geo_latitude":
        return property.geo_latitude || "";
      case "geo_longitude":
        return property.geo_longitude || "";
      case "video":
        return property.video || "";
      case "3d_mapping":
        return property["3d_mapping"] || "";
      case "client_type":
        return property.client_type || "";
      case "days_in_status":
        // Return days in status from API response
        return property.days_in_status != null ? property.days_in_status : "";
      case "account_manager":
        // Return account manager name from API response
        return property.account_manager_name || "";
      default:
        return "";
    }
  };

  // Fetch every record matching the current filter/search/sort (bypasses pagination)
  // The datatable's SQL can return duplicate rows per property (polymorphic user
  // join), and a tight LIMIT cuts off rows mid-duplicate — leaving unique
  // properties in the truncated tail. Always over-request so the dedupe step
  // sees the full set.
  const fetchAllFilteredProperties = async () => {
    const datatableParams = {
      draw: draw,
      start: 0,
      length: 100000,
      search: { value: debouncedSearch },
      order: [{ column: getSortColumnIndex(sortField), dir: sortDirection }],
      statusFilter: statusFilter === "all" ? "" : statusFilter,
      visibleColumns: visibleColumns,
    };

    const res = await fetch(`${process.env.REACT_APP_API_URL}/api/properties/datatable`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      body: JSON.stringify(datatableParams),
    });

    if (!res.ok) {
      throw new Error(`Datatable request failed (${res.status})`);
    }

    const data = await res.json();
    const propertiesData = data.data || [];
    return propertiesData.filter(
      (property, index, self) => index === self.findIndex((p) => p.id === property.id)
    );
  };

  // Export handlers - Export ALL columns (visible + hidden) matching Laravel export
  const handleExportCSV = async () => {
    if (!canExportProperties) return;
    try {
      setLoading(true);
      const allFiltered = await fetchAllFilteredProperties();

      const csvRows = [];
      const headers = allColumns.map((col) => col.label);
      csvRows.push(headers.join(","));

      allFiltered.forEach((property) => {
        const row = allColumns.map((col) => {
          const value = getPropertyValue(property, col.key);
          return `"${String(value).replace(/"/g, '""')}"`;
        });
        csvRows.push(row.join(","));
      });

      // Use Blob to avoid URL length limits on large exports
      const blob = new Blob(["﻿" + csvRows.join("\n")], {
        type: "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", `properties-${new Date().toISOString().split("T")[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success(`CSV exported successfully (${allFiltered.length} rows)!`);
    } catch (error) {
      console.error("Error exporting CSV:", error);
      toast.error("Failed to export CSV. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = async () => {
    if (!canExportProperties) return;
    try {
      setLoading(true);
      const allFiltered = await fetchAllFilteredProperties();

      // Try to import jsPDF and autoTable
      const jsPDF = (await import("jspdf")).default;
      let autoTable = null;
      try {
        await import("jspdf-autotable");
        // autoTable extends jsPDF prototype automatically
        autoTable = true;
      } catch (autoTableError) {
        console.log("autoTable not available, using fallback rendering");
      }

      const doc = new jsPDF();
      let yPos = 20;

      // Title
      doc.setFontSize(16);
      doc.text("Properties Report", 14, yPos);
      yPos += 10;

      // Date
      doc.setFontSize(10);
      doc.text(`Generated: ${new Date().toLocaleDateString('en-GB')}`, 14, yPos);
      yPos += 10;

      // Status Overview Summary
      doc.setFontSize(12);
      doc.text("Status Overview", 14, yPos);
      yPos += 6;
      doc.setFontSize(10);
      doc.text(`All: ${statusCounts.all || 0}`, 14, yPos);
      yPos += 5;
      doc.text(`Pre-Offer: ${statusCounts.pre_offer || 0} | Under Offer: ${statusCounts.under_offer || 0}`, 14, yPos);
      yPos += 5;
      doc.text(`Full: ${statusCounts.full || 0} | Empty: ${statusCounts.empty || 0}`, 14, yPos);
      yPos += 10;

      // Table with ALL columns (matching Laravel: exportOptions: { columns: '.pdf-export' })
      const tableColumns = allColumns.map((col) => col.label);
      const tableData = allFiltered.map((property) =>
        allColumns.map((col) => {
          const value = getPropertyValue(property, col.key);
          return String(value || "").substring(0, 50); // Limit to 50 chars per cell
        })
      );

      if (autoTable && typeof doc.autoTable === "function") {
        doc.autoTable({
          head: [tableColumns],
          body: tableData,
          startY: yPos,
          styles: { fontSize: 6, cellPadding: 1 }, // Smaller font for 34 columns
          headStyles: { fillColor: [66, 139, 202] },
          margin: { left: 10, right: 10 },
          columnStyles: {
            0: { cellWidth: 30 }, // Property Name
            1: { cellWidth: 20 }, // Property ID
          },
        });
      } else {
        // Fallback: Simple table rendering
        doc.setFontSize(8);
        doc.text("Table with all columns (simplified view)", 14, yPos);
        yPos += 5;
        allFiltered.slice(0, 20).forEach((property, idx) => {
          doc.text(`${idx + 1}. ${property.name || property.property_id}`, 14, yPos);
          yPos += 5;
          if (yPos > 280) {
            doc.addPage();
            yPos = 20;
          }
        });
      }

      // Save PDF
      doc.save(`properties-${new Date().toISOString().split("T")[0]}.pdf`);
      toast.success(`PDF exported successfully (${allFiltered.length} rows)!`);
    } catch (error) {
      console.error("Error exporting PDF:", error);
      toast.error("Failed to export PDF. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleColDropdownToggle = () => setShowColDropdown((v) => !v);

  const handleColDropdownBlur = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) setShowColDropdown(false);
  };

  if (initialLoading) return <Loader />;

  return (
    <div className={styles.container}>
      <div className="panel panel-default panel-form">
        <div className={styles.breadcrumbRow}>
          <a href="/dashboard" target="_blank" rel="noopener noreferrer" className={styles.breadcrumbMain}>Dashboard</a>
          <span className={styles.breadcrumbSep}>/</span>
          <span className={styles.breadcrumbCurrent}>Properties</span>
        </div>

        <div className="panel-heading panel-title">
          <h3 className="mr-auto">Properties overview</h3>
        </div>
        <div className="panel-body">
          <div className={styles.statusCardsContainer}>
            {STATUS_CARDS.map((card) => (
              <div className={styles.propertyOverviewContainer} key={card.key}>
                <div className={styles.propertyOverviewRow}>
                  <div
                    className={`${styles.propertyOverviewCard} ${statusFilter === card.key ? styles.active : ''}`}
                    style={{ background: card.color }}
                    onClick={() => handleStatusFilter(card.key)}
                  >
                    <div className={styles.propertyOverviewValue}>
                      {statusCounts[card.key] || 0}
                    </div>
                    <div className={styles.propertyOverviewLabel}>
                      {card.label}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Email Guardians Block Dropdown - Matching Laravel: @if(in_array('properties.emails.guardians', $globalPermissions)) */}
          {hasPermission && hasPermission("properties.emails.guardians") && (
            <div className={styles.emailGuardiansBlock}>
              <span><strong>Send email to guardians of block: </strong></span>
              <select
                className="form-control"
                value={selectedBlock}
                onChange={handleBlockChange}
              >
                <option value="">Please select</option>
                {Object.entries(blocks).map(([id, name]) => (
                  <option key={id} value={id}>{name}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className={styles.propertyTable}>
          <div className={styles.propertyTableHeader}>
            <h3 className={styles.propertyTableTitle}>Properties</h3>
            <div className={styles.headerButtons}>
              <button
                className={styles.btnSecondary}
                onClick={() => window.open("/properties/create", '_blank')}
              >
                <i className="fa fa-plus" /> Add Property
              </button>
              <button
                className={styles.btnSecondary}
                onClick={() => window.open("/guardians/emails/status/create", '_blank')}
              >
                <i className="fa fa-envelope" /> Email guardians of status
              </button>
            </div>
          </div>
          <div className={styles.propertyTableToolbar}>
            <div className={styles.propertyTableEntries}>
              Show
              <select
                className={styles.entriesSelect}
                value={pageSize}
                onChange={handlePageSizeChange}
              >
                {[10, 25, 50, 100].map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
              entries
            </div>
            <div className={styles.propertyTableSearch}>
              Search:
              <input
                type="text"
                className={styles.searchInput}
                placeholder=""
                value={search}
                onChange={handleSearchChange}
              />
              {loading && (
                <span style={{ position: 'absolute', right: '10px', fontSize: '12px', color: '#999' }}>
                  ⏳
                </span>
              )}
            </div>
            <button
              className={styles.btnExport}
              onClick={handleExportCSV}
              style={{ display: canExportProperties ? undefined : "none" }}
            >
              Export as CSV
            </button>
            <button
              className={styles.btnExport}
              onClick={handleExportPDF}
              style={{ display: canExportProperties ? undefined : "none" }}
            >
              Export as PDF
            </button>
            {canRestoreProperties && (
              <button
                className={styles.btnExport}
                onClick={handleToggleShowDeleted}
                style={{ backgroundColor: showDeleted ? "#d9534f" : undefined }}
              >
                {showDeleted ? "Show active properties" : "Show removed properties"}
              </button>
            )}
            <div style={{ display: "inline-block", position: "relative" }}>
              <button
                className={styles.btnColVisibility}
                onClick={handleColDropdownToggle}
                tabIndex={0}
              >
                Column visibility
              </button>
              {showColDropdown && (
                <div
                  className={styles.colVisibilityDropdown}
                  tabIndex={-1}
                  onBlur={handleColDropdownBlur}
                >
                  {allColumns.map((col) => (
                    <div
                      key={col.key}
                      className={`${styles.colVisibilityItem} ${visibleColumns.includes(col.key) ? styles.active : ''}`}
                      onClick={() => {
                        setVisibleColumns((cols) =>
                          cols.includes(col.key)
                            ? cols.filter((c) => c !== col.key)
                            : [...cols, col.key]
                        );
                      }}
                    >
                      {col.label}
                    </div>
                  ))}
                  <div className={styles.colVisibilityItem} style={{ color: "#888", cursor: "not-allowed", fontWeight: "bold" }}>
                    Actions
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className={styles.dataTableWrapper}>
            {loading && (
              <div className={styles.loadingOverlay}>
                Loading...
              </div>
            )}
            <table className={styles.table}>
              <thead>
                <tr>
                  {/* Dynamically render all visible columns in order */}
                  {allColumns
                    .filter((col) => visibleColumns.includes(col.key))
                    .map((col) => {
                      // Only sortable columns (first 6 that have backend support)
                      const sortableColumns = ["property_name", "property_id", "company_name", "postcode", "status", "notice_date"];
                      const isSortable = sortableColumns.includes(col.key);
                      return (
                        <th
                          key={col.key}
                          onClick={isSortable ? () => handleSort(col.key) : undefined}
                          style={isSortable ? { cursor: "pointer" } : { cursor: "default" }}
                        >
                          {col.label}
                          {isSortable && (
                            <SortIcon
                              direction={sortField === col.key ? sortDirection : null}
                              isActive={sortField === col.key}
                            />
                          )}
                        </th>
                      );
                    })}
                  {/* Actions column always at the end */}
                  <th className={styles.actionHead}>Actions</th>
                </tr>
              </thead>
              <tbody style={{ opacity: loading ? 0.5 : 1 }}>
                {properties.length === 0 ? (
                  <tr>
                    <td colSpan={visibleColumns.length + 1} style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                      {loading ? 'Loading...' : 'No properties found'}
                    </td>
                  </tr>
                ) : (
                  properties.map((property) => (
                    <tr key={property.id}>
                      {/* Dynamically render all visible columns in order */}
                      {allColumns
                        .filter((col) => visibleColumns.includes(col.key))
                        .map((col) => (
                          <td key={col.key}>
                            {col.key === "property_name" ? (
                              <a
                                href={`/properties/${property.id}`}
                                target="_blank" rel="noopener noreferrer"
                                style={{ color: "#337ab7", textDecoration: "none" }}
                              >
                                {getPropertyValue(property, col.key)}
                              </a>
                            ) : col.key === "website_display_name" ? (
                              <input
                                type="text"
                                defaultValue={property.website_display_name || ""}
                                placeholder="—"
                                style={{
                                  width: "100%",
                                  minWidth: "120px",
                                  border: "1px solid #ddd",
                                  borderRadius: "3px",
                                  padding: "2px 6px",
                                  fontSize: "12px",
                                }}
                                onBlur={(e) => {
                                  const newVal = e.target.value.trim();
                                  if (newVal !== (property.website_display_name || "")) {
                                    fetch(
                                      `${process.env.REACT_APP_API_URL}/api/properties/${property.id}/inline-update`,
                                      {
                                        method: "PATCH",
                                        headers: {
                                          "Content-Type": "application/json",
                                          Authorization: `Bearer ${localStorage.getItem("token")}`,
                                        },
                                        body: JSON.stringify({ website_display_name: newVal }),
                                      }
                                    )
                                      .then((r) => r.json())
                                      .then((data) => {
                                        if (data.success) {
                                          property.website_display_name = newVal;
                                          toast.success("Website name saved");
                                        } else {
                                          toast.error("Failed to save");
                                        }
                                      })
                                      .catch(() => toast.error("Failed to save"));
                                  }
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") e.target.blur();
                                }}
                              />
                            ) : col.key === "company_name" && property.client?.id ? (
                              <a
                                href={`/clients/${property.client.id}`}
                                target="_blank" rel="noopener noreferrer"
                                style={{ color: "#337ab7", textDecoration: "none" }}
                              >
                                {getPropertyValue(property, col.key)}
                              </a>
                            ) : (
                              getPropertyValue(property, col.key)
                            )}
                          </td>
                        ))}
                      {/* Actions column always at the end */}
                      <td>
                        <div className={styles.actionButtons}>
                          {showDeleted ? (
                            canRestoreProperties && (
                              <button className={`${styles.btnAction} ${styles.restore}`} onClick={() => handleRestore(property)}>Restore</button>
                            )
                          ) : (
                            <>
                          <button className={`${styles.btnAction} ${styles.show}`} onClick={() => window.open(`/properties/${property.id}`, '_blank')}>Show</button>
                          {!isGuardianManager() && (
                            <button className={`${styles.btnAction} ${styles.edit}`} onClick={() => window.open(`/properties/${property.id}/edit`, '_blank')}>Edit</button>
                          )}
                          {!isGuardianManager() && (
                            <button
                              className={`${styles.btnAction} ${styles.remove}`}
                              onClick={() => {
                                if (window.confirm("Are you sure you want to remove?")) {
                                  fetch(`${process.env.REACT_APP_API_URL}/api/properties/${property.id}/destroy`, {
                                    method: "DELETE",
                                    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
                                  })
                                    .then(async (response) => {
                                      const data = await response.json().catch(() => ({}));
                                      if (response.ok) {
                                        toast.success(data.message || "Property deleted successfully");
                                        fetchProperties();
                                      } else {
                                        toast.error(data.message || "Failed to remove property");
                                      }
                                    })
                                    .catch((error) => {
                                      console.error("Error deleting property:", error);
                                      toast.error("Failed to remove property. Please try again.");
                                    });
                                }
                              }}
                            >
                              Remove
                            </button>
                          )}
                          {!isGuardianManager() && (
                            <button className={styles.btnAction} onClick={() => window.open(`/properties/${property.id}/payments`, '_blank')}>Payments</button>
                          )}
                          {!isGuardianManager() && (
                            <button className={`${styles.btnAction} ${styles.photos}`} onClick={() => window.open(`/properties/${property.id}/photos`, '_blank')}>Photos</button>
                          )}
                          <button className={`${styles.btnAction} ${styles.viewings}`} onClick={() => window.open(`/properties/${property.id}/viewings`, '_blank')}>Viewings</button>
                          <button className={`${styles.btnAction} ${styles.inspections}`} onClick={() => window.open(`/properties/${property.id}/monthly-inspections`, '_blank')}>
                            Monthly Inspections
                          </button>
                          <button
                            className={`${styles.btnAction} ${styles.copyAddress}`}
                            onClick={() => {
                              const address = property.address_1 || property.address || "";
                              navigator.clipboard.writeText(address);
                              toast.success("Address copied to clipboard!");
                            }}
                          >
                            <i className="glyphicon glyphicon-road" style={{ marginRight: 4 }}></i>
                            Copy address
                          </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className={styles.pagination}>
            {renderPagination()}
          </div>

          <div className={styles.paginationInfo}>
            Showing {totalRecords === 0 ? 0 : startIndex + 1} to{" "}
            {Math.min(startIndex + pageSize, totalRecords)} of {totalRecords} entries
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyList;
